/**
 * What a draft write moves in the React Query cache.
 *
 * ## The defect this exists to close
 *
 * A draft write changes TWO cached things that are not the thing being written:
 * the Drafts list, and the folder rail's Drafts badge. `useComposerDraft`
 * (autosave) touched neither, which is the whole of the reported symptom —
 * "create a draft, navigate to the draft page, it doesn't reflect realtime…
 * and when it reflects the draft item the draft count doesn't reflect":
 *
 *   * the **list** polls on a 60s `refetchInterval` (`usePersonalInboxEmails`,
 *     `useInboxMessages`), so a new draft appeared "after a delay" — up to a
 *     minute;
 *   * the **rail's count** has no interval at all and sets
 *     `refetchOnWindowFocus: false`, so it never moved on its own.
 *
 * `staleTime` is not a timer. It does not refetch anything; it only decides
 * whether a REMOUNT refetches. Compose is its own route, so navigating back to
 * the inbox does remount the rail — but with its data cached under 30 seconds
 * ago it is still fresh, and React Query correctly declines. Two stale caches,
 * two different reasons, one missing invalidation.
 *
 * ## Why invalidating from the composer is cheap
 *
 * Neither the rail nor the list is mounted on `/inbox/compose` or
 * `/inbox/:inboxId/compose` — they are separate routes. `invalidateQueries`
 * therefore has no active observer to refetch and simply marks the cached
 * entries stale; the refetch happens on the navigation that asks to see them.
 * So this can run on every autosave without turning a 1s debounce into a
 * request loop.
 *
 * A leaf module by design: it imports the key factory and nothing else, so the
 * composer can invalidate without importing `AuthContext`.
 */
import { mailFolderKeys } from './mailFolderKeys';

/**
 * Invalidate everything a draft write moves, for one lane.
 *
 * `inboxId` is the lane, exactly as it is everywhere else in this subsystem:
 * present means a unified inbox, absent/null means personal. The lists are
 * invalidated by PREFIX so every filter and page variant goes with them —
 * drafts are served through the same list endpoints as mail, so they are
 * cached under the same keys.
 */
export function invalidateDrafts(queryClient, inboxId = null) {
    if (!queryClient) return;
    queryClient.invalidateQueries({ queryKey: mailFolderKeys.lane(inboxId || null) });
    if (inboxId) {
        queryClient.invalidateQueries({ queryKey: ['inbox', inboxId, 'messages'] });
    } else {
        queryClient.invalidateQueries({ queryKey: ['personal-inbox', 'emails'] });
    }
}

export default invalidateDrafts;
